import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { createProfile, updateProfile, fetchProfile, formatApiErrorDetail } from "@/lib/api";

const TYPES = ["Restaurant", "Chef", "Foodie", "Food Truck", "Caterer", "Bartender", "Brewery", "Bakery"];
const CITIES = ["Houston", "Atlanta", "Dallas", "Los Angeles", "New Orleans", "New York", "San Diego", "Golden Triangle"];

export default function ProfileForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", type: "Restaurant", bio: "", city: "Houston", neighborhood: "",
    cover_image: "", website: "", instagram: "", tiktok: "", youtube: "", twitter: "", facebook: "",
    gallery: "", signature_items: "",
  });

  useEffect(() => {
    if (editing) {
      fetchProfile(id)
        .then((p) =>
          setForm({
            name: p.name || "", type: p.type || "Restaurant", bio: p.bio || "",
            city: p.city || "Houston", neighborhood: p.neighborhood || "",
            cover_image: p.cover_image || "", website: p.website || "", instagram: p.instagram || "",
            tiktok: p.tiktok || "", youtube: p.youtube || "", twitter: p.twitter || "", facebook: p.facebook || "",
            gallery: (p.gallery || []).join(", "), signature_items: (p.signature_items || []).join(", "),
          })
        )
        .catch(() => navigate("/dashboard"));
    }
  }, [editing, id, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = {
      ...form,
      gallery: form.gallery.split(",").map((s) => s.trim()).filter(Boolean),
      signature_items: form.signature_items.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      const result = editing ? await updateProfile(id, payload) : await createProfile(payload);
      toast.success(editing ? "Profile updated" : "Profile created");
      navigate(`/profile/${result.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-sand-300 px-4 py-3 bg-white focus:border-brand-500 outline-none";
  const labelCls = "block font-mono text-[10px] tracking-[0.2em] uppercase text-sand-700 mb-2";

  return (
    <div data-testid="profile-form-page" className="max-w-2xl mx-auto px-6 py-16 md:py-24">
      <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-4">
        {editing ? "Edit Profile" : "New Profile"}
      </div>
      <h1 className="font-serif font-black text-4xl md:text-5xl leading-[1.02] text-sand-900 mb-10">
        {editing ? "Refine your story." : "Build your profile."}
      </h1>

      <form onSubmit={submit} className="space-y-6">
        {error && (
          <div data-testid="profile-form-error" className="bg-brand-50 border border-brand-500 text-brand-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}
        <div>
          <label className={labelCls}>Name</label>
          <input data-testid="profile-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Type</label>
            <select data-testid="profile-type-select" value={form.type} onChange={set("type")} className={inputCls}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>City</label>
            <select data-testid="profile-city-select" value={form.city} onChange={set("city")} className={inputCls}>
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>Neighborhood</label>
          <input data-testid="profile-neighborhood-input" value={form.neighborhood} onChange={set("neighborhood")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Bio</label>
          <textarea data-testid="profile-bio-input" rows={5} value={form.bio} onChange={set("bio")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Cover Image URL</label>
          <input data-testid="profile-cover-input" value={form.cover_image} onChange={set("cover_image")} className={inputCls} placeholder="https://…" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Website</label>
            <input data-testid="profile-website-input" value={form.website} onChange={set("website")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Instagram</label>
            <input data-testid="profile-instagram-input" value={form.instagram} onChange={set("instagram")} className={inputCls} placeholder="@handle" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <label className={labelCls}>TikTok</label>
            <input data-testid="profile-tiktok-input" value={form.tiktok} onChange={set("tiktok")} className={inputCls} placeholder="@handle" />
          </div>
          <div>
            <label className={labelCls}>YouTube</label>
            <input data-testid="profile-youtube-input" value={form.youtube} onChange={set("youtube")} className={inputCls} placeholder="@channel" />
          </div>
          <div>
            <label className={labelCls}>X / Twitter</label>
            <input data-testid="profile-twitter-input" value={form.twitter} onChange={set("twitter")} className={inputCls} placeholder="@handle" />
          </div>
          <div>
            <label className={labelCls}>Facebook</label>
            <input data-testid="profile-facebook-input" value={form.facebook} onChange={set("facebook")} className={inputCls} placeholder="page name" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Signature Items (comma separated)</label>
          <input data-testid="profile-signature-input" value={form.signature_items} onChange={set("signature_items")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Gallery Image URLs (comma separated)</label>
          <input data-testid="profile-gallery-input" value={form.gallery} onChange={set("gallery")} className={inputCls} />
        </div>

        <button
          data-testid="profile-submit-button"
          disabled={busy}
          className="w-full bg-sand-900 text-sand-50 px-5 py-4 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors disabled:opacity-60"
        >
          {busy ? "Saving…" : editing ? "Save Changes" : "Create Profile"}
        </button>
      </form>
    </div>
  );
}
